import type { FormField } from './types';

function validateRequired(field: FormField, value: unknown): string | null {
  if (!field.required) return null;

  if (field.type === 'checkbox-multiple') {
    const isEmpty = !value || !Array.isArray(value) || value.length === 0;
    return isEmpty ? `${field.label} es requerido` : null;
  }

  if (field.type === 'checkbox') {
    return value !== true ? `${field.label} es requerido` : null;
  }

  const isBlank = !value || (typeof value === 'string' && value.trim() === '');
  return isBlank ? `${field.label} es requerido` : null;
}

function validateNumberType(field: FormField, value: unknown): string | null {
  if (field.type !== 'number') return null;
  const hasNoValue = value === '' || value === null || value === undefined;
  if (hasNoValue) return null;

  const numValue = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numValue)) {
    return `${field.label}: ingrese un número válido`;
  }
  return null;
}

function validateNumberRange(field: FormField, value: unknown): string | null {
  const typeError = validateNumberType(field, value);
  if (typeError) return typeError;
  if (field.type !== 'number') return null;

  const hasNoValue = value === '' || value === null || value === undefined;
  if (hasNoValue) return null;

  const numValue = Number(value);
  if (field.validation?.min !== undefined && numValue < field.validation.min) {
    return `${field.label} debe ser mayor o igual a ${field.validation.min}`;
  }
  if (field.validation?.max !== undefined && numValue > field.validation.max) {
    return `${field.label} debe ser menor o igual a ${field.validation.max}`;
  }
  return null;
}

function validateDateType(field: FormField, value: unknown): string | null {
  if (field.type !== 'date') return null;
  const hasNoValue = value === '' || value === null || value === undefined;
  if (hasNoValue) return null;

  const text = String(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return `${field.label}: use el formato de fecha AAAA-MM-DD`;
  }
  const parsed = Date.parse(text);
  if (Number.isNaN(parsed)) {
    return `${field.label}: la fecha no es válida`;
  }
  return null;
}

function validatePattern(field: FormField, value: unknown): string | null {
  if (field.type !== 'text' || !value || !field.validation?.pattern) return null;

  try {
    const regex = new RegExp(field.validation.pattern);
    if (!regex.test(String(value))) {
      return `${field.label} no cumple con el formato requerido`;
    }
  } catch {
    return `${field.label}: el patrón de validación configurado no es válido`;
  }
  return null;
}

/** Validación de un campo dinámico (requerido, tipo y reglas). */
export function validateSurveyField(field: FormField, value: unknown): string | null {
  const requiredError = validateRequired(field, value);
  if (requiredError) return requiredError;

  const numberError = validateNumberRange(field, value);
  if (numberError) return numberError;

  const dateError = validateDateType(field, value);
  if (dateError) return dateError;

  return validatePattern(field, value);
}
